/**
 * Business Bay-2 Building Data
 * All measurements in FEET (1 unit = 1 foot).
 *
 * Coordinate system (per floor-plan compass rose, RHS rendering):
 *   +X = East,  -X = West
 *   +Y = Up
 *   +Z = South (toward 200ft road),  -Z = North (toward 25ft road)
 *
 * Building footprint is L-shaped:
 *   - Full north block: ~289ft wide (E-W) × ~118ft deep (N-S)
 *   - South-west block: ~197ft wide × ~72ft deep
 *   - Low-X low-Z corner (SE) has a cutout (~92ft × 72ft)
 */

// ─── ELEMENT TYPES (color map for 2D editor + 3D) ───
export const ELEMENT_TYPES = {
  Showroom:    { color: '#2a6b3a', label: 'Showroom' },
  Office:      { color: '#3a5a6b', label: 'Office' },
  Passage:     { color: '#4a4a4a', label: 'Passage' },
  WC:          { color: '#2a5a5a', label: 'WC' },
  Lift:        { color: '#6b4a2a', label: 'Lift' },
  Stairs:      { color: '#5a3a2a', label: 'Stairs' },
  Parking:     { color: '#3a3a3a', label: 'Parking' },
  'AC Duct':   { color: '#2a6b6b', label: 'AC Duct' },
  Void:        { color: '#5a2a2a', label: 'Void' },
  'Special Area': { color: '#4a3a6b', label: 'Special Area' },
};

// ─── BUILDING DIMENSIONS ───
export const BUILDING = {
  name: 'Business Bay-2',
  location: 'Nr. Samarpan Circle, Jamnagar',

  // Overall footprint (bounding box) in feet
  width: 289,   // East-West ~289 ft (along X axis)
  depth: 345,   // North-South ~345 ft (along Z axis)

  // L-shape cutout position (NW corner, low-X low-Z)
  cutX: 121,    // NW cutout extends 121ft from west edge (x=0)
  cutZ: 72,     // NW cutout extends 72ft from north edge (z=0)

  // Floor heights (in feet)
  floors: {
    '-2': { name: 'Basement 02', height: 9.83, elevation: -19.66, type: 'parking' },
    '-1': { name: 'Basement 01', height: 9.83, elevation: -9.83, type: 'parking' },
    '0':  { name: 'Ground Floor', height: 14 + 9.83, elevation: 0, type: 'showroom' },
    '2':  { name: '2nd Floor', height: 9.83, elevation: 14 + 9.83, type: 'mixed' },
    '3':  { name: '3rd Floor', height: 9.83, elevation: 14 + 9.83 * 2, type: 'mixed' },
    '4':  { name: '4th Floor', height: 9.83, elevation: 14 + 9.83 * 3, type: 'mixed' },
  },

  // Total building height (feet)
  totalHeight: 14 + 9.83 * 4, // ~53.32 ft
};

// ─── BUILDING FOOTPRINT (editable in 2D editor) ───
export const FOOTPRINT_OUTER = [
  { x: 118.5, z: 155 },
  { x: 200,   z: 155 },
  { x: 200,   z: 65 },
  { x: 231.5, z: 65 },
  { x: 231.5, z: 0 },
  { x: 289,   z: 0 },
  { x: 289,   z: 241.5 },
  { x: 118.5, z: 344.5 },
];

// ─── WALKWAY OUTLINE (editable in 2D editor) ───
// Parallelogram walkway along the diagonal facade, 13ft wide, slanted ends
export const WALKWAY_OUTLINE = [
  { x: 289,    z: 241.5 },
  { x: 118.5,  z: 344.5 },
  { x: 118.5,  z: 350.5 },
  { x: 289,    z: 247.5 },
];

// ─── STAIR OUTLINE (editable in 2D editor) ───
// Parallelogram stair area along walkway outer edge, stepping down to road level
export const STAIR_OUTLINE = [
  { x: 289,   z: 247.5 },
  { x: 118.5, z: 350.5 },
  { x: 118.5, z: 355.5 },
  { x: 289,   z: 252.5 },
];
export let STAIR_NUM_STEPS = 4;
export function setStairNumSteps(n) { STAIR_NUM_STEPS = n; }

// ─── SURFACE PARKING (legacy config — kept for reference) ───
// export const SURFACE_PARKING = {
//   innerOffset: 4, roadMargin: 6, maxParkDepth: 40, groupWidth: 13,
//   gapWidth: 5, spotWidth: 9, angle: 60, endFraction: 0.70,
// };

// ─── GROUND PAD OUTLINE (editable triangle between stairs and road) ───
export const GROUND_PAD_OUTLINE = [
  { x: 289, z: 252.5 },
  { x: 289, z: 272.5 },
  { x: 118.5, z: 375.5 },
  { x: 118.5, z: 355.5 },
];

// ─── PARKING GROUPS (individually placed parallelogram parking areas) ───
export const PARKING_GROUPS = [
  { id: 'PKG-1', label: 'Parking 1', vertices: [{ x: 134.22441767895054, z: 347.9850197607818 }, { x: 140.63468600789562, z: 360.4367039218913 }, { x: 133.81540745065791, z: 364.60312601611446 }, { x: 127.4111511453142, z: 352.1777991660965 }], angle: 60, spotWidth: 9 },
  { id: 'PKG-2', label: 'Parking 7', vertices: [{ x: 276.7212124462405, z: 262.38454050366704 }, { x: 284.28787911290715, z: 274.1178738370004 }, { x: 258.45454577957383, z: 289.2845405036671 }, { x: 250.98787911290714, z: 277.7178738370004 }], angle: 60, spotWidth: 9 },
  { id: 'PKG-3', label: 'Parking 6', vertices: [{ x: 248.106439885969, z: 279.5085180851315 }, { x: 255.38787911290714, z: 291.45120717033376 }, { x: 229.68787911290715, z: 306.8845405036671 }, { x: 222.50871691359578, z: 295.07644041666566 }], angle: 60, spotWidth: 9 },
  { id: 'PKG-4', label: 'Parking 5', vertices: [{ x: 219.84882756245875, z: 296.78493215585854 }, { x: 226.9671294291254, z: 308.86731295585855 }, { x: 220.0829822291254, z: 312.9416041558585 }, { x: 213.0002132331835, z: 300.8834943131928 }], angle: 60, spotWidth: 9 },
  { id: 'PKG-5', label: 'Parking 4', vertices: [{ x: 200.7137269242226, z: 307.6395815284361 }, { x: 207.74706025755594, z: 319.7062481951028 }, { x: 200.94706025755593, z: 323.93958152843607 }, { x: 193.94210942662133, z: 311.86238127023614 }], angle: 60, spotWidth: 9 },
  { id: 'PKG-6', label: 'Parking 3', vertices: [{ x: 191.10959117672317, z: 313.51092260494886 }, { x: 197.77625784338983, z: 325.87758927161553 }, { x: 172.11414550213374, z: 341.4544824405923 }, { x: 165.47692980338522, z: 329.1614422447905 }], angle: 60, spotWidth: 9 },
  { id: 'PKG-7', label: 'Parking 2', vertices: [{ x: 162.57969247143131, z: 330.9391049626646 }, { x: 169.22721906571823, z: 343.2947198444731 }, { x: 143.50962535956083, z: 358.76948969631565 }, { x: 136.90605053470142, z: 346.4466555888349 }], angle: 60, spotWidth: 9 },
];

// ─── PLACED PLANTERS (individually placed planters with optional trees) ───
export const PLACED_PLANTERS = [
  { id: 'PL-1',  label: 'Stair Planter 1',  x: 146.03782020709164, z: 336.3697438343267, diameter: 4.5, height: 4.5, hasTree: true, shape: 'rect', width: 3, depth: 3, angle: 30 },
  { id: 'PL-2',  label: 'Stair Planter 2',  x: 176.5, z: 318, diameter: 4.5, height: 4.5, hasTree: true, shape: 'rect', width: 3, depth: 3, angle: 30 },
  { id: 'PL-3',  label: 'Gap Planter 3',    x: 197.7716945141766, z: 305.1835472193559, diameter: 4.5, height: 4.5, hasTree: true, shape: 'rect', width: 3, depth: 3, angle: 30 },
  { id: 'PL-4',  label: 'Stair Planter 4',  x: 212.28968929581686, z: 296.3920481793492, diameter: 4.5, height: 4.5, hasTree: true, shape: 'rect', width: 3, depth: 3, angle: 30 },
  { id: 'PL-5',  label: 'Gap Planter 5',    x: 250.16666666666666, z: 279.70000000000005, diameter: 4.5, height: 4.5, hasTree: true, shape: 'rect', width: 2.5, depth: 2.5, angle: 123 },
  { id: 'PL-6',  label: 'Stair Planter 6',  x: 262.10102126933333, z: 266.119094528, diameter: 4.5, height: 4.5, hasTree: true, shape: 'rect', width: 3, depth: 3, angle: 30 },
  { id: 'PL-7',  label: 'Gap Planter 1',    x: 126.8537912011141, z: 359.8909207125145, diameter: 4.5, height: 4.5, hasTree: true, shape: 'rect', width: 16, depth: 2, angle: 117 },
  { id: 'PL-8',  label: 'Stair Planter 8',  x: 221.86114877105447, z: 297.116022278008, diameter: 4.5, height: 4.5, hasTree: true, shape: 'rect', width: 2.5, depth: 2.5, angle: 121 },
  { id: 'PL-9',  label: 'Gap Planter 9',    x: 164.6544471290042, z: 331.16833412822837, diameter: 4.5, height: 4.5, hasTree: true, shape: 'rect', width: 2.5, depth: 2.5, angle: 119 },
  { id: 'PL-10', label: 'Stair Planter 10', x: 141.46142234569388, z: 358.3844645833387, diameter: 2.5, height: 4.5, hasTree: true, shape: 'rect', width: 2.5, depth: 2.5, angle: 122 },
  { id: 'PL-11', label: 'Gap Planter 11',   x: 136.19896896607574, z: 348.4182141512362, diameter: 4.5, height: 4.5, hasTree: true, shape: 'rect', width: 2.5, depth: 2.5, angle: 119 },
  { id: 'PL-12', label: 'Stair Planter 12', x: 198.6386666666667, z: 323.7866666666667, diameter: 4.5, height: 4.5, hasTree: true, shape: 'rect', width: 2.5, depth: 2.5, angle: 122 },
  { id: 'PL-13', label: 'Gap Planter 13',   x: 233.66456917333335, z: 283.35854408533334, diameter: 4.5, height: 4.5, hasTree: true, shape: 'rect', width: 3, depth: 3, angle: 30 },
  { id: 'PL-14', label: 'Stair Planter 14', x: 227.66170359447662, z: 306.76918053834004, diameter: 4.5, height: 4.5, hasTree: true, shape: 'rect', width: 2.5, depth: 2.5, angle: 122 },
  { id: 'PL-15', label: 'Road Planter 15',  x: 193.17049006175571, z: 313.7373365586603, diameter: 5, height: 4.5, hasTree: true, shape: 'rect', width: 2.5, depth: 2.5, angle: 119 },
  { id: 'PL-16', label: 'Road Planter 16',  x: 170.11178684415998, z: 341.24609813162664, diameter: 5, height: 4.5, hasTree: true, shape: 'rect', width: 2.5, depth: 2.5, angle: 122 },
  { id: 'PL-17', label: 'Stair Planter 0',  x: 122.75765430996039, z: 350.50110241384476, diameter: 5, height: 4.5, hasTree: true, shape: 'rect', width: 3, depth: 3, angle: 30 },
  { id: 'PL-18', label: 'Road Planter 18',  x: 256.1757638077704, z: 289.26313270898004, diameter: 5, height: 4.5, hasTree: true, shape: 'rect', width: 2.5, depth: 2.5, angle: 122 },
  { id: 'PL-19', label: 'Road Planter 19',  x: 286.57075404202084, z: 251.38320779295069, diameter: 5, height: 4.5, hasTree: true, shape: 'rect', width: 3, depth: 3, angle: 30 },
  { id: 'PL-20', label: 'Planter 20',       x: 281.8958599129072, z: 266.3007346370004, diameter: 5, height: 4.5, hasTree: true, shape: 'rect', width: 16, depth: 2, angle: 123 },
];

// ─── ROAD WALKWAY (elevated strip near road, parallel to diagonal) ───
export const ROAD_WALKWAY = {
  innerOffset: 22,      // ft from stair outer edge
  outerOffset: 25,      // ft from stair outer edge (3ft wide)
  startFraction: 0,
  endFraction: 0.70,
  height: 0.5,          // ft elevation above road
};

// ─── WALKWAY PLANTERS (on 4ft elevated walkway along diagonal) ───
export const WALKWAY_PLANTERS = [
  { along: 0.15, width: 8, depth: 4, height: 2.5 },
  { along: 0.30, width: 8, depth: 4, height: 2.5 },
  { along: 0.50, width: 10, depth: 4, height: 2.5 },
  { along: 0.70, width: 8, depth: 4, height: 2.5 },
  { along: 0.85, width: 8, depth: 4, height: 2.5 },
];

// Building sections (for roof slabs, section overlays)
export const BUILDING_SECTIONS = [
  // South block (main showroom area)
  { x: 118.5, z: 155, w: 170.5, d: 189.5, label: 'South Block' },
  // Middle block
  { x: 200, z: 65, w: 89, d: 90, label: 'Middle Block' },
  // North block (east wing showrooms)
  { x: 231.5, z: 0, w: 57.5, d: 65, label: 'North Block' },
];

// ─── SURROUNDING ROADS (legacy box approach — kept for reference) ───
export const ROADS = [
  { name: '200 FT Wide Road (South)', x: -66, z: 358, w: 420, d: 200, direction: 'EW' },
  { name: '200 FT Wide Road (West)',  x: -213, z: -66, w: 200, d: 620, direction: 'NS' },
  { name: '30 FT Wide Road (East)',   x: 302, z: -33, w: 30, d: 420, direction: 'NS' },
  { name: '25 FT Wide Road (North)',  x: 108, z: -38, w: 214, d: 25, direction: 'EW' },
];

// ─── PLACED ROADS (centerline-based, editable in 2D editor) ───
export const PLACED_ROADS = [
  {
    id: 'RD-2',
    label: '200 FT Wide Road',
    points: [ { x: 411.1855463099936, z: 229.36765274538283 }, { x: 59.34022116382117, z: 441.4832070044367 } ],
    width: 50,
    curved: true,
    medianPlanters: true,
    medianPlanterWidth: 20,
    medianPlanterDepth: 8,
    medianPlanterSpacing: 55,
    medianPlanterHeight: 2.5,
    medianHasTrees: true,
    medianPlanterAngle: 90,
    medianPlanterWidthPct: 40,
    medianPlanterGapPct: 50,
    edgeStripeWidth: 0.5,
    footpathWidth: 6,
  },
];

// ─── CORE (Elevators, Stairs, Passages) ───
export const CORE = {
  // Main central core between showroom groups 04 and 05
  // Located between the 9'0" passage (z=81) and the showroom back walls
  x: 68, z: 81, w: 40, d: 40,
  elements: [
    { type: 'elevator', x: 75, z: 95, w: 8, d: 8, label: 'Lift' },
    { type: 'elevator', x: 85, z: 95, w: 8, d: 8, label: 'Lift' },
    { type: 'elevator', x: 95, z: 95, w: 8, d: 8, label: 'Lift' },
    { type: 'elevator', x: 75, z: 107, w: 8, d: 8, label: 'Lift' },
    { type: 'stairs', x: 68, z: 85, w: 17.5, d: 17.58, label: 'Passage (17\'6" × 17\'7")' },
    { type: 'stairs', x: 88, z: 85, w: 2.13, d: 14, label: 'Passage (2\'1½" × 14\'0")' },
    { type: 'passage', x: 68, z: 107, w: 40, d: 9, label: 'Passage (9\'10")' },
  ]
};

// ─── GROUND FLOOR UNITS (Height 14'0" — Double Height) ───
// Showroom positions derived from floor plan (page_07.png)
// Coordinate: x,z = NW corner of showroom interior; w = E-W width; d = N-S depth
// South facade at z=190, east facade at x=289

export const GROUND_FLOOR = {
  showrooms: [
    // ── South-block showrooms 01-14 (polygon — diagonal south facade) ──
    { id: 'GF-01', x: 118.5, z: 237.5, w: 14, d: 107, label: 'Showroom 01', dim: "14'0\" × 107'6\"", type: 'Showroom',
      vertices: [{ x: 118.5, z: 237.5 }, { x: 132.5, z: 237.5 }, { x: 132.5, z: 336 }, { x: 118.5, z: 344.5 }] },
    { id: 'GF-02', x: 132.5, z: 237.5, w: 12, d: 98.5, label: 'Showroom 02', dim: "12'0\" × 100'1\"", type: 'Showroom',
      vertices: [{ x: 132.5, z: 237.5 }, { x: 144.5, z: 237.5 }, { x: 144.5, z: 328.5 }, { x: 132.5, z: 336 }] },
    { id: 'GF-03', x: 144.5, z: 237.5, w: 12, d: 91.5, label: 'Showroom 03', dim: "12'0\" × 93'3\"", type: 'Showroom',
      vertices: [{ x: 144.5, z: 237.5 }, { x: 156.5, z: 237.5 }, { x: 156.5, z: 321.5 }, { x: 144.5, z: 329 }] },
    { id: 'GF-04', x: 156.5, z: 221.5, w: 12, d: 100, label: 'Showroom 04', dim: "12'0\" × 98'11\"", type: 'Showroom',
      vertices: [{ x: 156.5, z: 221.5 }, { x: 168.5, z: 221.5 }, { x: 168.5, z: 314.5 }, { x: 156.5, z: 321.5 }] },
    { id: 'GF-05', x: 168.5, z: 201.5, w: 12, d: 113, label: 'Showroom 05', dim: "12'0\" × 110'5\"", type: 'Showroom',
      vertices: [{ x: 168.5, z: 201.5 }, { x: 180.5, z: 201.5 }, { x: 180.5, z: 307 }, { x: 168.5, z: 314.5 }] },
    { id: 'GF-06', x: 180.5, z: 201.5, w: 12, d: 105.5, label: 'Showroom 06', dim: "12'0\" × 104'0\"", type: 'Showroom',
      vertices: [{ x: 180.5, z: 201.5 }, { x: 192.5, z: 201.5 }, { x: 192.5, z: 300 }, { x: 180.5, z: 307 }] },
    { id: 'GF-07', x: 192.5, z: 201.5, w: 12, d: 98.5, label: 'Showroom 07', dim: "12'0\" × 97'2\"", type: 'Showroom',
      vertices: [{ x: 192.5, z: 201.5 }, { x: 204.5, z: 201.5 }, { x: 204.5, z: 292.5 }, { x: 192.5, z: 300 }] },
    { id: 'GF-08', x: 204.5, z: 201.5, w: 12, d: 91, label: 'Showroom 08', dim: "12'0\" × 90'4\"", type: 'Showroom',
      vertices: [{ x: 204.5, z: 201.5 }, { x: 216.5, z: 201.5 }, { x: 216.5, z: 285.5 }, { x: 204.5, z: 292.5 }] },
    { id: 'GF-09', x: 216.5, z: 201.5, w: 12, d: 84, label: 'Showroom 09', dim: "12'0\" × 83'6\"", type: 'Showroom',
      vertices: [{ x: 216.5, z: 201.5 }, { x: 228.5, z: 201.5 }, { x: 228.5, z: 278 }, { x: 216.5, z: 285.5 }] },
    { id: 'GF-10', x: 228.5, z: 201.5, w: 11.5, d: 76.5, label: 'Showroom 10', dim: "12'0\" × 76'8\"", type: 'Showroom',
      vertices: [{ x: 228.5, z: 201.5 }, { x: 240, z: 201.5 }, { x: 240, z: 271 }, { x: 228.5, z: 278 }] },
    { id: 'GF-11', x: 240, z: 165, w: 12, d: 106, label: 'Showroom 11', dim: "12'0\" × 105'1\"", type: 'Showroom',
      vertices: [{ x: 240, z: 165 }, { x: 252, z: 165 }, { x: 252, z: 264 }, { x: 240, z: 271 }] },
    { id: 'GF-12', x: 252, z: 165, w: 12, d: 99, label: 'Showroom 12', dim: "12'0\" × 98'3\"", type: 'Showroom',
      vertices: [{ x: 252, z: 165 }, { x: 264, z: 165 }, { x: 264, z: 256.5 }, { x: 252, z: 264 }] },
    { id: 'GF-13', x: 264, z: 165, w: 12, d: 91.42, label: 'Showroom 13', dim: "12'0\" × 91'5\"", type: 'Showroom',
      vertices: [{ x: 264, z: 165 }, { x: 276, z: 165 }, { x: 276, z: 249.5 }, { x: 264, z: 256.42 }] },
    { id: 'GF-14', x: 276, z: 165, w: 13.2, d: 84.25, label: 'Showroom 14', dim: "13'2½\" × 84'3\"", type: 'Showroom',
      vertices: [{ x: 276, z: 165 }, { x: 289.2, z: 165 }, { x: 289, z: 241.5 }, { x: 276, z: 249.25 }] },

    // ── East-wing showrooms 15-23 (rectangles) ──
    { id: 'GF-15', x: 231.42, z: 129, w: 57.58, d: 12, label: 'Showroom 15', dim: "57'1\" × 12'0\"", type: 'Showroom' },
    { id: 'GF-16', x: 231.42, z: 117, w: 57.58, d: 12, label: 'Showroom 16', dim: "57'1\" × 12'0\"", type: 'Showroom' },
    { id: 'GF-17', x: 231.42, z: 105, w: 57.58, d: 12, label: 'Showroom 17', dim: "57'1\" × 12'0\"", type: 'Showroom' },
    { id: 'GF-18', x: 231.5, z: 72.5, w: 57.5, d: 12, label: 'Showroom 18', dim: "57'1\" × 12'0\"", type: 'Showroom' },
    { id: 'GF-19', x: 231.5, z: 60.5, w: 57.5, d: 12, label: 'Showroom 19', dim: "57'1\" × 12'0\"", type: 'Showroom' },
    { id: 'GF-20', x: 231.5, z: 48.5, w: 57.5, d: 12, label: 'Showroom 20', dim: "57'1\" × 12'0\"", type: 'Showroom' },
    { id: 'GF-21', x: 231.5, z: 36.5, w: 57.5, d: 12, label: 'Showroom 21', dim: "57'1\" × 12'0\"", type: 'Showroom' },
    { id: 'GF-22', x: 231.5, z: 24.5, w: 57.5, d: 12, label: 'Showroom 22', dim: "57'1\" × 12'0\"", type: 'Showroom' },
    { id: 'GF-23', x: 231.5, z: 0, w: 57.5, d: 24.33, label: 'Showroom 23', dim: "57'1\" × 24'4\"", type: 'Showroom' },
  ],

  specialAreas: [
    { id: 'RECEPTION', x: 228, z: 141, w: 61, d: 24, label: 'Double Height Reception', dim: "24'6\" × 24'6\"", color: [0.8, 0.7, 0.4], type: 'Special Area' },
    { id: 'BANQUET', x: 118.5, z: 155, w: 74, d: 66.5, label: 'Banquet Hall', color: [0.5, 0.4, 0.3], type: 'Special Area',
      vertices: [{ x: 118.5, z: 155 }, { x: 192.5, z: 155 }, { x: 192.5, z: 201.5 }, { x: 168.5, z: 201.5 }, { x: 168.5, z: 221.5 }, { x: 118.5, z: 221.5 }] },
    { id: 'GYM', x: 200, z: 84.5, w: 31.5, d: 35, label: 'Gym', color: [0.3, 0.5, 0.4], type: 'Special Area' },
    { id: 'GAME', x: 200, z: 119.5, w: 26.5, d: 22, label: 'Game Zone', color: [0.3, 0.4, 0.5], type: 'Special Area' },
    { id: 'STEAM', x: 210, z: 75.5, w: 13.5, d: 9, label: 'Steam Room', color: [0.4, 0.4, 0.5], type: 'Special Area' },

    // Passages
    { id: 'PASSAGE_CORE', x: 192.5, z: 119.5, w: 47.5, d: 82, label: 'Passage (9\'10" Wide)', color: [0.6, 0.6, 0.6], type: 'Passage',
      vertices: [{ x: 192.5, z: 155 }, { x: 200, z: 155 }, { x: 200, z: 141.5 }, { x: 226.5, z: 141.5 }, { x: 226.5, z: 119.5 }, { x: 231.5, z: 119.5 }, { x: 231.5, z: 141 }, { x: 228, z: 141 }, { x: 228, z: 165 }, { x: 240, z: 165 }, { x: 240, z: 201.5 }, { x: 192.5, z: 201.5 }] },
    { id: 'PS-1', x: 118.5, z: 221.5, w: 38, d: 16, label: 'Passage 1', type: 'Passage' },
    { id: 'PS-2', x: 200, z: 72, w: 10, d: 7.5, label: 'Passage 2', type: 'Passage' },
    { id: 'PS-3', x: 210, z: 71, w: 7, d: 4.5, label: 'Passage 3', type: 'Passage' },
    { id: 'PS-4', x: 217, z: 71, w: 6.5, d: 4.5, label: 'Passage 4', type: 'Passage' },
    { id: 'PS-5', x: 210, z: 65, w: 21.5, d: 6, label: 'Passage 5', type: 'Passage' },

    // WC
    { id: 'WC-1', x: 152.5, z: 225.5, w: 6.5, d: 5.5, label: 'WC 1', type: 'WC' },
    { id: 'WC-2', x: 152.5, z: 231, w: 6.5, d: 6.5, label: 'PAN.', type: 'WC' },
    { id: 'WC-3', x: 151.5, z: 242, w: 5, d: 3.5, label: 'WC 3', type: 'WC' },
    { id: 'WC-4', x: 223.5, z: 71, w: 8, d: 13.5, label: 'WC 4', type: 'WC' },
    { id: 'WC-5', x: 205, z: 79.5, w: 5, d: 5, label: 'WC 5', type: 'WC' },
    { id: 'WC-6', x: 200, z: 79.5, w: 5, d: 5, label: 'WC 6', type: 'WC' },
    { id: 'WC-7', x: 231.5, z: 16, w: 7, d: 4, label: 'WC 7', type: 'WC' },
    { id: 'WC-8', x: 231.5, z: 24.5, w: 7, d: 4, label: 'WC 8', type: 'WC' },
    { id: 'WC-9', x: 231.5, z: 42, w: 7, d: 4, label: 'WC 9', type: 'WC' },
    { id: 'WC-10', x: 231.5, z: 50.5, w: 7, d: 4, label: 'WC 10', type: 'WC' },
    { id: 'WC-11', x: 231.5, z: 66, w: 7, d: 4, label: 'WC 11', type: 'WC' },
    { id: 'WC-12', x: 231.5, z: 74.5, w: 7, d: 4, label: 'WC 12', type: 'WC' },

    // Voids
    { id: 'VD-1', x: 152.5, z: 221.5, w: 6.5, d: 4, label: 'Void 1', type: 'Void' },
    { id: 'VD-2', x: 153, z: 245.5, w: 3.5, d: 3.5, label: 'Void 2', type: 'Void' },
    { id: 'VD-3', x: 231.5, z: 20, w: 7, d: 4.5, label: 'Void 3', type: 'Void' },
    { id: 'VD-4', x: 231.5, z: 46, w: 7, d: 4.5, label: 'Void 4', type: 'Void' },
    { id: 'VD-5', x: 231.5, z: 70, w: 7, d: 4.5, label: 'Void 5', type: 'Void' },

    // Lifts
    { id: 'LF-1', x: 156.5, z: 237.5, w: 5, d: 4.5, label: 'Lift 1', type: 'Lift' },
    { id: 'LF-2', x: 151.5, z: 237.5, w: 5, d: 4.5, label: 'Lift 2', type: 'Lift' },

    // Stairs
    { id: 'ST-1', x: 165, z: 237.5, w: 3.5, d: 20, label: 'Stairs 1', type: 'Stairs' },
    { id: 'ST-2', x: 144.5, z: 237.5, w: 3.5, d: 20, label: 'Stairs 2', type: 'Stairs' },
    { id: 'ST-3', x: 200, z: 9.5, w: 24, d: 55.5, label: 'Stairs 3', type: 'Stairs' },
    { id: 'ST-4', x: 267.5, z: 94.5, w: 21.5, d: 10.5, label: 'Stairs 4', type: 'Stairs' },
  ],
};

// ─── UPPER FLOOR UNITS ───
// Upper floors repeat the showroom layout for east wing
// South block becomes offices on upper floors
export const UPPER_FLOOR = {
  showrooms: [
    // East-wing showrooms (same layout as GF 15-23 but numbered 101+)
    { id: 'S-101', x: 231.42, z: 129,   w: 57.08, d: 12,    label: 'Showroom 101', type: 'Showroom' },
    { id: 'S-102', x: 231.42, z: 117,   w: 57.08, d: 12,    label: 'Showroom 102', type: 'Showroom' },
    { id: 'S-103', x: 231.42, z: 105,   w: 57.08, d: 12,    label: 'Showroom 103', type: 'Showroom' },
    { id: 'S-104', x: 231.42, z: 77.33, w: 57.08, d: 12,    label: 'Showroom 104', type: 'Showroom' },
    { id: 'S-105', x: 231.42, z: 65.33, w: 57.08, d: 12,    label: 'Showroom 105', type: 'Showroom' },
    { id: 'S-106', x: 231.42, z: 53.33, w: 57.08, d: 12,    label: 'Showroom 106', type: 'Showroom' },
    { id: 'S-107', x: 231.42, z: 41.33, w: 57.08, d: 12,    label: 'Showroom 107', type: 'Showroom' },
    { id: 'S-108', x: 231.42, z: 29.33, w: 57.08, d: 12,    label: 'Showroom 108', type: 'Showroom' },
    { id: 'S-109', x: 231.42, z: 5,     w: 57.08, d: 24.33, label: 'Showroom 109', type: 'Showroom' },
  ],

  offices: [
    // South-block offices (west wing)
    { id: 'O-201', x: 18,  z: 82,  w: 25, d: 17.3, label: 'Office 201', dim: "25' × 17'4\"", type: 'Office' },
    { id: 'O-202', x: 18,  z: 99,  w: 25, d: 17.3, label: 'Office 202', dim: "25' × 17'4\"", type: 'Office' },
    { id: 'O-203', x: 18,  z: 116, w: 25, d: 12,   label: 'Office 203', dim: "25' × 12'", type: 'Office' },
    { id: 'O-204', x: 18,  z: 128, w: 25, d: 12,   label: 'Office 204', dim: "25' × 12'", type: 'Office' },

    // South-block offices (center)
    { id: 'O-101', x: 108, z: 82,  w: 42, d: 12, label: 'Office 101', dim: "42' × 12'", type: 'Office' },
    { id: 'O-102', x: 108, z: 94,  w: 42, d: 12, label: 'Office 102', dim: "42' × 12'", type: 'Office' },
    { id: 'O-103', x: 108, z: 106, w: 42, d: 12, label: 'Office 103', dim: "42' × 12'", type: 'Office' },
    { id: 'O-104', x: 108, z: 118, w: 42, d: 12, label: 'Office 104', dim: "42' × 12'", type: 'Office' },
    { id: 'O-105', x: 150, z: 82,  w: 42, d: 12, label: 'Office 105', dim: "42' × 12'", type: 'Office' },
    { id: 'O-106', x: 150, z: 94,  w: 42, d: 12, label: 'Office 106', dim: "42' × 12'", type: 'Office' },
    { id: 'O-107', x: 150, z: 106, w: 42, d: 12, label: 'Office 107', dim: "42' × 12'", type: 'Office' },
    { id: 'O-108', x: 150, z: 118, w: 21.5, d: 12, label: 'Office 108', dim: "21'6\" × 12'", type: 'Office' },

    // North wing offices
    { id: 'O-109', x: 92,  z: 13, w: 39, d: 12, label: 'Office 109', dim: "39' × 12'", type: 'Office' },
    { id: 'O-110', x: 92,  z: 25, w: 39, d: 12, label: 'Office 110', dim: "39' × 12'", type: 'Office' },
    { id: 'O-111', x: 92,  z: 37, w: 39, d: 12, label: 'Office 111', dim: "39' × 12'", type: 'Office' },
    { id: 'O-112', x: 92,  z: 49, w: 39, d: 12, label: 'Office 112', dim: "39' × 12'", type: 'Office' },
    { id: 'O-113', x: 135, z: 13, w: 39, d: 12, label: 'Office 113', dim: "39' × 12'", type: 'Office' },
    { id: 'O-114', x: 135, z: 25, w: 39, d: 12, label: 'Office 114', dim: "39' × 12'", type: 'Office' },
    { id: 'O-115', x: 135, z: 37, w: 39, d: 12, label: 'Office 115', dim: "39' × 12'", type: 'Office' },
    { id: 'O-116', x: 135, z: 49, w: 39, d: 12, label: 'Office 116', dim: "39' × 12'", type: 'Office' },
    { id: 'O-117', x: 135, z: 61, w: 39, d: 12, label: 'Office 117', dim: "39' × 12'", type: 'Office' },
  ],

  southShowrooms: [],

  passages: [
    { id: 'P-1', x: 0,   z: 72, w: 92,  d: 9, label: 'Passage (9\'0")' },
    { id: 'P-E', x: 225, z: 0,  w: 6.3, d: 141, label: 'Passage (East 6\'3")' },
    { id: 'P-2', x: 180, z: 79, w: 9,   d: 110, label: 'Passage (NS 8\'8")' },
    { id: 'P-3', x: 68,  z: 107, w: 40, d: 9, label: 'Passage (Core)' },
    { id: 'P-W', x: 0,   z: 72, w: 7,   d: 118, label: 'Passage (West 7\'0")' },
  ],
};

// ─── BASEMENT LAYOUT ───
export const BASEMENT = {
  parkingRows: [
    { x: 16,  z: 16,  w: 65, d: 16, slots: 8, label: 'Parking Row A' },
    { x: 16,  z: 40,  w: 65, d: 16, slots: 8, label: 'Parking Row B' },
    { x: 16,  z: 98,  w: 65, d: 16, slots: 8, label: 'Parking Row C' },
    { x: 16,  z: 122, w: 65, d: 16, slots: 8, label: 'Parking Row D' },
    { x: 16,  z: 154, w: 65, d: 16, slots: 8, label: 'Parking Row E' },
    { x: 197, z: 16,  w: 82, d: 16, slots: 10, label: 'Parking Row F' },
    { x: 197, z: 40,  w: 82, d: 16, slots: 10, label: 'Parking Row G' },
    { x: 197, z: 66,  w: 82, d: 16, slots: 10, label: 'Parking Row H' },
    { x: 197, z: 92,  w: 82, d: 16, slots: 10, label: 'Parking Row I' },
    { x: 197, z: 118, w: 82, d: 16, slots: 10, label: 'Parking Row J' },
    { x: 197, z: 144, w: 82, d: 16, slots: 10, label: 'Parking Row K' },
  ],
  driveways: [
    { x: 92, z: 0,  w: 26, d: 190, label: 'Driveway (Central)' },
    { x: 174, z: 0, w: 16, d: 190, label: 'Driveway (East)' },
  ],
  ramps: [
    { x: 131, z: 16, w: 33, d: 13, label: 'Ramp to Ground Floor' },
    { x: 131, z: 33, w: 33, d: 13, label: 'Ramp to Basement 02' },
  ],
  conferenceHall: { x: 92, z: 72, w: 26, d: 20, label: "Conference Hall (23' × 20')" },
};

// ─── EXTERIOR FEATURES ───
export const EXTERIOR = {
  glassFacade: {
    panels: { width: 5, height: 4, gap: 0.33 },
    tint: [0.6, 0.75, 0.85, 0.4],
  },

  canopy: {
    x: 180, z: 339, w: 66, d: 13, h: 16,
    label: 'Main Entrance Canopy',
  },

  trees: [
    // South-road trees removed (replaced by parking island + walkway planter trees)
    // Along east road
    { x: 302, z: 16 }, { x: 302, z: 49 }, { x: 302, z: 82 },
    { x: 302, z: 115 }, { x: 302, z: 148 },
    // Along north road
    { x: 115, z: -16 }, { x: 164, z: -16 }, { x: 213, z: -16 }, { x: 262, z: -16 },
  ],

  terraceGarden: {
    x: 128, z: 165, w: 151, d: 170,
    label: '35,000 Sq Ft Terrace Garden',
  },
};

// ─── AMENITIES ───
export const AMENITIES = {
  gym:        { floor: 0, x: 200, z: 84.5, w: 31.5, d: 35, label: 'Gym' },
  cafeteria:  { floor: 0, x: 170, z: 85, w: 10, d: 16, label: 'Cafeteria' },
  reception:  { floor: 0, x: 228, z: 141, w: 61, d: 24, label: 'Reception (Double Height)' },
  lounge:     { floor: 0, x: 200, z: 119.5, w: 26.5, d: 22, label: 'Elite Lounge / Game Zone' },
};

// ─── SPECIFICATIONS (for material assignment) ───
export const SPECS = {
  flooring: {
    offices:     'Premium vitrified tiles',
    showrooms:   'Premium vitrified tiles',
    foyer:       'Designer tiles',
    passages:    'Designer tiles',
    parking:     'Concrete',
  },
  walls: {
    interior: 'Wall putty + paint',
    exterior: 'Glass curtain wall + aluminium cladding',
  },
  windows: 'Aluminium anodized / powder coated',
  doors: {
    offices:   'Wooden doors',
    showrooms: 'Rolling shutters',
  },
};

// ─── ITEM GROUPS (for 2D editor grouping feature) ───
export const ITEM_GROUPS = [];
